import React, { useEffect, useRef, useState } from "react";
import { doc, onSnapshot, runTransaction, serverTimestamp, collection, addDoc, query, where, getDocs, updateDoc } from "firebase/firestore";
import "./css/Contador.css";
import { db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartSimple, faList, faCalendarPlus } from "@fortawesome/free-solid-svg-icons";
import { mostrarAviso, mostrarError, mostrarExito, mostrarInfo } from "../../utils/Alertas";
import { useDispatch } from "react-redux";
import { hideLoading, showLoading } from "../../../redux/actions/loadingActions";
import Modal from "../secondary/Modal";

const Contador: React.FC = () => {
    const dispatch = useDispatch();
    const [value, setValue] = useState<number | null>(null);
    const navigate = useNavigate();
    const isOptimisticUpdate = useRef(false);

    const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
    const [periodsOpen, setPeriodsOpen] = useState<any[]>([]);
    const [selectedPeriodId, setSelectedPeriodId] = useState<string>("");
    const [originalStartDate, setOriginalStartDate] = useState<string>("");

    const [startDate, setStartDate] = useState(
        new Date().toISOString().split("T")[0],
    );
    const [endDate, setEndDate] = useState("");

    const counterRef = doc(db, "counter", "main");
    const heartShape = confetti.shapeFromPath(
        "M167 72c19-38 37-56 65-56 35 0 63 28 63 63 0 74-128 150-128 150S39 153 39 79c0-35 28-63 63-63 28 0 46 18 65 56z",
    );

    useEffect(() => {
        const unsubscribe = onSnapshot(counterRef, (snap) => {
            if (snap.exists()) {
                if (!isOptimisticUpdate.current) {
                    setValue(snap.data().value);
                }
            } else {
                setValue(0);
            }
        });

        return unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [counterRef]);

    useEffect(() => {
        if (!isPeriodModalOpen) return;

        const q = query(
            collection(db, "period_logs"),
            where("endDate", "==", null),
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            const data = snap.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setPeriodsOpen(data);
        });

        return () => unsubscribe();
    }, [isPeriodModalOpen]);

    const shootHearts = () => {
        confetti({
            particleCount: 40,
            spread: 80,
            origin: { y: 0.6 },
            shapes: [heartShape],
            colors: ["#ff4d6d", "#ff758f", "#ff8fab"],
            scalar: 1.2,
        });
    };

    const closeModalPeriod = () => {
        setIsPeriodModalOpen(false);
        handleSelectPeriod('');
    };

    const increment = async () => {
        if (value === null) return;

        isOptimisticUpdate.current = true;
        setValue((prev) => (prev !== null ? prev + 1 : prev));
        shootHearts();

        try {
            await runTransaction(db, async (transaction) => {
                const counterSnap = await transaction.get(counterRef);
                const currentValue = counterSnap.exists()
                    ? counterSnap.data().value
                    : 0;

                const newValue = currentValue + 1;

                transaction.set(counterRef, {
                    value: newValue,
                    updatedAt: serverTimestamp(),
                });

                await addDoc(collection(db, "counter_logs"), {
                    increment: 1,
                    totalAfter: newValue,
                    createdAt: serverTimestamp(),
                });
            });
        } catch (error) {
            // Rollback
            setValue((prev) => (prev !== null ? prev - 1 : prev));
        } finally {
            isOptimisticUpdate.current = false;
        }
    };

    const handleSelectPeriod = (periodId: string) => {
        setSelectedPeriodId(periodId);

        const selected = periodsOpen.find((p) => p.id === periodId);
        if (selected) {
            setStartDate(selected.startDate);
            setOriginalStartDate(selected.startDate);
            setEndDate("");
        } else {
            setStartDate(new Date().toISOString().split("T")[0]);
            setEndDate("");
            setOriginalStartDate("");
        }
    };

    const savePeriod = async () => {
        if (!startDate) return;

        dispatch(showLoading());
        try {
            if (selectedPeriodId) {
                // 👉 Si no ha cambiado la fecha inicio
                if (startDate === originalStartDate) {

                    // 👉 Y no hay fecha fin informada
                    if (!endDate) {
                        mostrarInfo("Debes informar una fecha de fin para cerrar el periodo.");
                        return;
                    }
                }

                // 👉 cerrar periodo existente
                await updateDoc(doc(db, "period_logs", selectedPeriodId), {
                    startDate,
                    endDate: endDate || null,
                });
            } else {
                 // 👉 comprobamos duplicado
                const q = query(
                    collection(db, "period_logs"),
                    where("startDate", "==", startDate)
                );

                const snap = await getDocs(q);

                if (!snap.empty) {
                    mostrarAviso("Ya existe un periodo con esa fecha de inicio.");
                    return;
                }

                // 👉 crear nuevo periodo
                await addDoc(collection(db, "period_logs"), {
                    startDate,
                    endDate: endDate || null,
                    createdAt: serverTimestamp(),
                });
            }
            mostrarExito('Periodo registrado correctamente.');
        } catch (error) {
            console.error("Error al guardar el periodo: ", error);
            mostrarError("Error al guardar el periodo.");
        } finally {
            dispatch(hideLoading());
        }

        setStartDate(new Date().toISOString().split("T")[0]);
        setIsPeriodModalOpen(false);
        setSelectedPeriodId("");
        setEndDate("");
    };

    const formatDate = (date: string) =>
        new Date(date).toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });

    return (
        <div className="container-fluid">
            <div className="text-center pb-3">
                <h2 className="page-heading">Contador del Amor</h2>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ pl: 0.5, mb: 1 }}
                >
                    Toca el número para incrementar el contador.
                </Typography>
            </div>

            <div className="container-fluid content pb-3">
                <div className="container-principal">
                    <h1 className="counter" onClick={increment}>
                        {value}
                    </h1>
                </div>

                <div className="bottomButtons">
                    <button
                        className="iconButton"
                        onClick={() => navigate("/contador/stats")}
                        aria-label="Dashboard"
                    >
                        <FontAwesomeIcon icon={faChartSimple} />
                    </button>

                    <button
                        className="iconButton"
                        onClick={() => setIsPeriodModalOpen(true)}
                        aria-label="Registrar periodo"
                    >
                        <FontAwesomeIcon icon={faCalendarPlus} />
                    </button>

                    <button
                        className="iconButton"
                        onClick={() => navigate("/contador/logs")}
                        aria-label="Registros"
                    >
                        <FontAwesomeIcon icon={faList} />
                    </button>
                </div>
            </div>

            {isPeriodModalOpen && (
                <Modal onClose={closeModalPeriod} className="modal-contador">
                    <div className="modal-body">
                        <h2>Registrar periodo</h2>

                        {/* DESPLEGABLE */}
                        {periodsOpen.length > 0 && (
                            <select
                                className="mb-3"
                                value={selectedPeriodId}
                                onChange={(e) =>
                                    handleSelectPeriod(e.target.value)
                                }
                            >
                                <option value="">Nuevo periodo</option>
                                {periodsOpen.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        Inicio: {formatDate(p.startDate)}
                                    </option>
                                ))}
                            </select>
                        )}

                        {/* FECHA INICIO */}
                        <label>Fecha inicio</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />

                        {/* FECHA FIN */}
                        <label>Fecha fin</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />

                        <button
                            className="botonPrimarioContador"
                            onClick={savePeriod}
                        >
                            Guardar
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default Contador;
